import { QuestionClaimStatus } from '@models/api/question';
import type { UserSummary } from '@models/api';

/**
 * Which side of an ask-on-behalf relationship the current viewer is on.
 * - `beneficiary`     — the person the question was posted FOR
 * - `original_asker`  — the person who posted it on someone else's behalf
 * - `observer`        — anyone else (or a self-asked question / signed-out viewer)
 */
export type AskOnBehalfRole = 'beneficiary' | 'original_asker' | 'observer';

/**
 * The subset of question fields the ask-on-behalf derivation needs. Deliberately
 * minimal so every DTO that carries these — `QuestionRead` (feed),
 * `QuestionDetailRead` (detail), and `QuestionProfileRead` (profile) — is
 * assignable without coupling the helper to any one shape.
 *
 * Note we key the creator off `created_by_user.id`, NOT `created_by_user_id`:
 * the latter is absent from `QuestionProfileRead`, and relying on it is exactly
 * what made the profile card's role derivation drift out of sync (the
 * original-asker banner never rendered there). Going through `created_by_user`
 * works on all three surfaces.
 */
export interface AskOnBehalfFields {
  claim_status: QuestionClaimStatus | null;
  asked_for_user_id: string | null;
  created_by_user: UserSummary | null;
  asked_for_user: UserSummary | null;
  /** Only present on feed/detail DTOs; used as a creator-only subtitle fallback. */
  asked_for_email?: string | null;
}

/**
 * The claim state of a question, with `self` standing in for "not asked on
 * behalf". A single discriminant instead of `isOnBehalf` + three claim booleans:
 * those four fields could express impossible combinations (e.g. claimed with a
 * pending subtitle); `kind` makes the state space exactly the four real cases.
 * Test `kind !== 'self'` for the old `isOnBehalf`, `kind === 'claimed'` for the
 * old `isClaimed`, etc.
 */
export type AskOnBehalfKind = 'self' | 'pending' | 'claimed' | 'declined';

/** Fields common to every claim state of the view. */
interface AskOnBehalfBase {
  /** The viewer's role in this question's ask-on-behalf relationship. */
  role: AskOnBehalfRole;
  /** Original poster (person who created the question on someone's behalf). */
  originalAsker: UserSummary | null;
  /** Beneficiary (person the question was asked for). */
  beneficiary: UserSummary | null;
}

/**
 * Presentation state for the author byline, modeled as a discriminated union on
 * `kind` so the subtitle's nullability is tied to the claim state instead of
 * being an independent `string | null`. A `claimed`/`self` view cannot carry an
 * "Asking for" subtitle and a `pending`/`declined` one always has copy, so those
 * cross-field impossible states are unrepresentable — mirroring the
 * discriminated-union style of `useAskOnBehalf`. Subtitle copy by state:
 * - self / claimed → `null` (no "asking for" line; the beneficiary owns claimed)
 * - pending        → "Asking for {beneficiary name}" (creator-only email fallback)
 * - declined       → "Asking for someone else" (never names the beneficiary)
 */
export type AskOnBehalfView =
  | (AskOnBehalfBase & { kind: 'self'; subtitle: null })
  | (AskOnBehalfBase & { kind: 'pending'; subtitle: string })
  | (AskOnBehalfBase & { kind: 'claimed'; subtitle: null })
  | (AskOnBehalfBase & { kind: 'declined'; subtitle: string });

/**
 * Exhaustiveness guard for the `kind` switch below: adding a new
 * `AskOnBehalfKind` without a matching `case` makes `assertNever(kind)` a compile
 * error (the `never` parameter stops accepting it). Matches the `assertNever`
 * idiom already used in AuthProvider / useRealtimeInvalidation.
 */
function assertNever(value: never): never {
  throw new Error(`Unhandled ask-on-behalf kind: ${String(value)}`);
}

/**
 * Single source of truth for ask-on-behalf presentation state. Pure and
 * surface-agnostic: it derives the viewer's role and the shared copy, but does
 * NOT decide banner visibility — each surface applies its own policy on top of
 * `role` + the claim flags, because those policies legitimately differ (the
 * feed shows observers the banner only once claimed; the profile shows the
 * original asker their banner in every state).
 */
export function getAskOnBehalfView(
  question: AskOnBehalfFields,
  viewerId: string | null | undefined,
): AskOnBehalfView {
  const { claim_status: claimStatus } = question;

  // `claim_status` is null only for self-asked questions, so it is the reliable
  // signal — `created_by_user` can't be (the backend sets it on every question,
  // self-asked included). Collapse it straight into the `kind` discriminant.
  let kind: AskOnBehalfKind = 'self';
  if (claimStatus === QuestionClaimStatus.pending) kind = 'pending';
  else if (claimStatus === QuestionClaimStatus.claimed) kind = 'claimed';
  else if (claimStatus === QuestionClaimStatus.declined) kind = 'declined';

  const creatorId = question.created_by_user?.id ?? null;
  const beneficiaryId = question.asked_for_user_id;

  let role: AskOnBehalfRole = 'observer';
  if (kind !== 'self' && viewerId) {
    if (beneficiaryId && beneficiaryId === viewerId) {
      role = 'beneficiary';
    } else if (creatorId && creatorId === viewerId) {
      role = 'original_asker';
    }
  }

  const base: AskOnBehalfBase = {
    role,
    originalAsker: question.created_by_user,
    beneficiary: question.asked_for_user,
  };

  // Build the subtitle inside an exhaustive switch on `kind` so the union's
  // per-state subtitle type is satisfied, and a future 5th kind trips
  // `assertNever` at compile time instead of silently rendering no subtitle.
  switch (kind) {
    case 'self':
      return { ...base, kind, subtitle: null };
    case 'claimed':
      // Beneficiary owns it now — no "asking for" line.
      return { ...base, kind, subtitle: null };
    case 'pending': {
      // Prefer the beneficiary's name; fall back to the invited email for the
      // original asker only (don't surface a stranger's email to others).
      const emailFallback = role === 'original_asker' ? question.asked_for_email : null;
      const name = question.asked_for_user?.full_name || emailFallback || 'someone else';
      return { ...base, kind, subtitle: `Asking for ${name}` };
    }
    case 'declined':
      // Never name the beneficiary once they've declined.
      return { ...base, kind, subtitle: 'Asking for someone else' };
    default:
      return assertNever(kind);
  }
}
